import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Mic, MicOff, Cat, History, Trash2, Volume2, Phone, Utensils, Gamepad2, Hand, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// ── Interpretaciones de maullidos ──
const INTERPRETACIONES = [
  { id: 'comida', texto: 'Quiero comida', emoji: '🍽️', color: '#E67E22' },
  { id: 'jugar', texto: 'Quiero jugar', emoji: '🧶', color: '#3498DB' },
  { id: 'atencion', texto: 'Necesito atencion', emoji: '💕', color: '#E91E63' },
  { id: 'enfadado', texto: 'Estoy molesto', emoji: '😾', color: '#E74C3C' },
  { id: 'dejame', texto: 'Dejame en paz', emoji: '🐾', color: '#95A5A6' },
  { id: 'curiosidad', texto: 'Tengo curiosidad', emoji: '🔍', color: '#9B59B6' },
  { id: 'contento', texto: 'Estoy contento', emoji: '😻', color: '#2ECC71' },
  { id: 'saludo', texto: 'Hola, me alegro de verte', emoji: '👋', color: '#F39C12' },
  { id: 'malestar', texto: 'No me encuentro bien', emoji: '🤒', color: '#7F8C8D' },
  { id: 'territorio', texto: 'Este es mi territorio', emoji: '🏠', color: '#D35400' },
];

function analizarMaullido(audioData) {
  const { frecuencia, intensidad, duracion } = audioData;
  let interpretacion;
  let confianza;

  if (intensidad > 0.7 && frecuencia > 500) {
    interpretacion = INTERPRETACIONES.find(i => i.id === 'comida');
    confianza = 75 + Math.random() * 20;
  } else if (intensidad > 0.7 && frecuencia < 300) {
    interpretacion = INTERPRETACIONES.find(i => i.id === 'enfadado');
    confianza = 70 + Math.random() * 20;
  } else if (intensidad > 0.5 && duracion > 2) {
    interpretacion = INTERPRETACIONES.find(i => i.id === 'atencion');
    confianza = 65 + Math.random() * 25;
  } else if (intensidad < 0.3 && frecuencia > 400) {
    interpretacion = INTERPRETACIONES.find(i => i.id === 'curiosidad');
    confianza = 60 + Math.random() * 25;
  } else if (intensidad < 0.3 && frecuencia < 300) {
    interpretacion = INTERPRETACIONES.find(i => i.id === 'contento');
    confianza = 70 + Math.random() * 20;
  } else if (duracion < 1 && intensidad > 0.4) {
    interpretacion = INTERPRETACIONES.find(i => i.id === 'saludo');
    confianza = 65 + Math.random() * 20;
  } else if (intensidad > 0.6 && duracion > 3) {
    interpretacion = INTERPRETACIONES.find(i => i.id === 'dejame');
    confianza = 60 + Math.random() * 25;
  } else if (frecuencia > 600 && duracion > 2) {
    interpretacion = INTERPRETACIONES.find(i => i.id === 'malestar');
    confianza = 55 + Math.random() * 25;
  } else if (intensidad > 0.5 && frecuencia > 350 && frecuencia < 500) {
    interpretacion = INTERPRETACIONES.find(i => i.id === 'jugar');
    confianza = 65 + Math.random() * 25;
  } else {
    interpretacion = INTERPRETACIONES.find(i => i.id === 'territorio');
    confianza = 55 + Math.random() * 30;
  }
  return { interpretacion, confianza: Math.min(Math.round(confianza), 95) };
}

// ── Generador de sonidos de gato con Web Audio API ──
function generarSonidoGato(tipo) {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const gain = ctx.createGain();
  gain.connect(ctx.destination);

  const configs = {
    llamar: { freqs: [600, 500, 650, 550], dur: 0.3, pause: 0.15, vol: 0.4, type: 'sine', reps: 4 },
    comida: { freqs: [700, 600, 750, 650, 700], dur: 0.5, pause: 0.08, vol: 0.5, type: 'sine', reps: 5 },
    juego: { freqs: [800, 900, 750, 850, 950], dur: 0.15, pause: 0.1, vol: 0.35, type: 'sine', reps: 5 },
    saludo: { freqs: [500, 600, 550], dur: 0.25, pause: 0.2, vol: 0.3, type: 'sine', reps: 3 },
    calma: { freqs: [120, 125, 118, 122], dur: 0.8, pause: 0.05, vol: 0.25, type: 'sawtooth', reps: 6 },
  };

  const c = configs[tipo] || configs.llamar;
  let t = ctx.currentTime;

  for (let i = 0; i < c.reps; i++) {
    const osc = ctx.createOscillator();
    const oscGain = ctx.createGain();
    osc.type = c.type;
    osc.frequency.setValueAtTime(c.freqs[i % c.freqs.length], t);

    if (tipo !== 'calma') {
      // Add vibrato for realistic meow
      const vibrato = ctx.createOscillator();
      const vibratoGain = ctx.createGain();
      vibrato.frequency.value = 5 + Math.random() * 3;
      vibratoGain.gain.value = 20 + Math.random() * 30;
      vibrato.connect(vibratoGain);
      vibratoGain.connect(osc.frequency);
      vibrato.start(t);
      vibrato.stop(t + c.dur);

      // Frequency slide
      osc.frequency.linearRampToValueAtTime(c.freqs[i % c.freqs.length] * (0.7 + Math.random() * 0.4), t + c.dur);
    }

    // Envelope
    oscGain.gain.setValueAtTime(0, t);
    oscGain.gain.linearRampToValueAtTime(c.vol, t + 0.02);
    oscGain.gain.setValueAtTime(c.vol, t + c.dur * 0.6);
    oscGain.gain.linearRampToValueAtTime(0, t + c.dur);

    osc.connect(oscGain);
    oscGain.connect(gain);
    osc.start(t);
    osc.stop(t + c.dur);

    t += c.dur + c.pause;
  }

  setTimeout(() => ctx.close(), (t - ctx.currentTime) * 1000 + 200);
}

// ── Componentes ──
function BotonGrabar({ grabando, onClick, nivel }) {
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        {grabando && (
          <div className="absolute inset-0 rounded-full animate-ping opacity-20" style={{ backgroundColor: 'var(--v-gold)', transform: `scale(${1 + nivel * 0.5})` }} />
        )}
        <button
          onClick={onClick}
          data-testid="btn-grabar"
          className="relative w-28 h-28 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl"
          style={{
            backgroundColor: grabando ? '#E74C3C' : 'var(--v-gold, #D4AF37)',
            color: grabando ? '#fff' : 'var(--v-bg, #0A0F0D)',
            transform: grabando ? `scale(${1 + nivel * 0.05})` : 'scale(1)',
          }}
        >
          {grabando ? <MicOff className="w-10 h-10" /> : <Mic className="w-10 h-10" />}
        </button>
      </div>
      <p className="text-base font-semibold" style={{ fontFamily: 'Cinzel, serif', color: 'var(--v-text)' }}>
        {grabando ? 'Escuchando...' : 'Grabar maullido'}
      </p>
      {grabando && (
        <div className="flex gap-1 items-end h-8">
          {[...Array(16)].map((_, i) => (
            <div key={i} className="w-1 rounded-full transition-all duration-75"
              style={{ backgroundColor: 'var(--v-gold)', height: `${Math.max(4, Math.random() * nivel * 100)}%`, minHeight: '4px' }} />
          ))}
        </div>
      )}
    </div>
  );
}

function ResultadoTraduccion({ resultado, onClose }) {
  if (!resultado) return null;
  const { interpretacion, confianza } = resultado;
  return (
    <Card className="border-2 overflow-hidden" style={{ borderColor: interpretacion.color + '40' }} data-testid="resultado-traduccion">
      <CardContent className="p-5 text-center space-y-3">
        <div className="text-5xl">{interpretacion.emoji}</div>
        <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--v-text-muted)', fontFamily: 'Cinzel, serif' }}>Traduccion</p>
        <p className="text-lg" style={{ color: 'var(--v-text)' }}>Tu gato puede estar diciendo:</p>
        <p className="text-xl font-bold italic" style={{ color: interpretacion.color }}>"{interpretacion.texto}"</p>
        <div className="space-y-1 pt-2">
          <div className="flex justify-between text-sm">
            <span style={{ color: 'var(--v-text-muted)' }}>Confianza</span>
            <span className="font-bold" style={{ color: interpretacion.color }}>{confianza}%</span>
          </div>
          <div className="w-full h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--v-surface2)' }}>
            <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${confianza}%`, backgroundColor: interpretacion.color }} />
          </div>
        </div>
        <Button onClick={onClose} className="mt-3" style={{ backgroundColor: 'var(--v-gold)', color: 'var(--v-bg)', fontFamily: 'Cinzel, serif' }} data-testid="btn-nueva-traduccion">
          Nuevo maullido
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Sonidos para comunicarse ──
const SONIDOS_GATO = [
  { id: 'llamar', label: 'Llamar al gato', emoji: '📢', icon: Phone, desc: 'Maullidos cortos para atraer su atencion', color: '#3498DB' },
  { id: 'comida', label: 'Sonido de comida', emoji: '🍽️', icon: Utensils, desc: 'Maullido insistente como pidiendo comida', color: '#E67E22' },
  { id: 'juego', label: 'Sonido de juego', emoji: '🧶', icon: Gamepad2, desc: 'Trinos rapidos y juguetones', color: '#2ECC71' },
  { id: 'saludo', label: 'Sonido de saludo', emoji: '👋', icon: Hand, desc: 'Miau amigable de bienvenida', color: '#F39C12' },
  { id: 'calma', label: 'Sonido de calma', emoji: '💤', icon: Heart, desc: 'Ronroneo suave y relajante', color: '#9B59B6' },
];

function PanelComunicar({ onPlay }) {
  const [tocando, setTocando] = useState(null);

  const reproducir = (sonido) => {
    setTocando(sonido.id);
    generarSonidoGato(sonido.id);
    onPlay(sonido);
    setTimeout(() => setTocando(null), 2500);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-center" style={{ color: 'var(--v-text-muted)' }}>
        Pulsa un boton y acerca el telefono a tu gato
      </p>
      <div className="space-y-3">
        {SONIDOS_GATO.map(s => {
          const Icon = s.icon;
          const activo = tocando === s.id;
          return (
            <button
              key={s.id}
              onClick={() => reproducir(s)}
              data-testid={`btn-sonido-${s.id}`}
              className="w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-300"
              style={{
                backgroundColor: activo ? s.color + '20' : 'var(--v-surface)',
                border: `1px solid ${activo ? s.color : 'var(--v-border)'}`,
                transform: activo ? 'scale(1.02)' : 'scale(1)',
              }}
            >
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: s.color + '20' }}>
                {activo ? (
                  <Volume2 className="w-6 h-6 animate-pulse" style={{ color: s.color }} />
                ) : (
                  <Icon className="w-6 h-6" style={{ color: s.color }} />
                )}
              </div>
              <div className="text-left flex-1">
                <p className="font-semibold text-sm" style={{ color: 'var(--v-text)', fontFamily: 'Cinzel, serif' }}>{s.label}</p>
                <p className="text-xs" style={{ color: 'var(--v-text-muted)' }}>{s.desc}</p>
              </div>
              <span className="text-2xl">{s.emoji}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HistorialPanel({ historial, onDelete }) {
  if (!historial.length) {
    return (
      <div className="text-center py-12">
        <Cat className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--v-text-muted)' }} />
        <p style={{ color: 'var(--v-text-muted)' }}>Sin interacciones todavia</p>
        <p className="text-sm" style={{ color: 'var(--v-text-muted)' }}>Graba un maullido o reproduce un sonido</p>
      </div>
    );
  }

  const iconoTipo = (tipo) => {
    if (tipo === 'traduccion') return '🎙️';
    if (tipo === 'sonido') return '🔊';
    return '🐱';
  };

  return (
    <div className="space-y-2" data-testid="historial-traducciones">
      {historial.map((item) => (
        <Card key={item.id} className="overflow-hidden">
          <CardContent className="p-3 flex items-center gap-3">
            <span className="text-xl">{item.emoji || iconoTipo(item.tipo)}</span>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate" style={{ color: 'var(--v-text)' }}>
                {item.tipo === 'traduccion' ? `"${item.interpretacion}"` : item.interpretacion}
              </p>
              <p className="text-xs" style={{ color: 'var(--v-text-muted)' }}>
                {new Date(item.fecha).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                {item.confianza ? ` · ${item.confianza}%` : ''}
                {' · '}{item.tipo === 'traduccion' ? 'Maullido analizado' : 'Sonido reproducido'}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onDelete(item.id)} data-testid={`btn-delete-${item.id}`}>
              <Trash2 className="w-4 h-4" style={{ color: 'var(--v-text-muted)' }} />
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Componente Principal ──
export default function MKPetLab() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [grabando, setGrabando] = useState(false);
  const [nivel, setNivel] = useState(0);
  const [resultado, setResultado] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [tab, setTab] = useState('traducir');
  const [countdown, setCountdown] = useState(0);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const animFrameRef = useRef(null);
  const audioDataRef = useRef({ frecuencia: 0, intensidad: 0, duracion: 0, muestras: [] });
  const startTimeRef = useRef(0);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { cargarHistorial(); }, []);

  const cargarHistorial = async () => {
    try {
      const res = await axios.get(`${API}/cats/translations`, { headers });
      setHistorial(res.data);
    } catch (e) { console.error(e); }
  };

  const guardarInteraccion = async (data) => {
    try {
      await axios.post(`${API}/cats/translations`, data, { headers });
      cargarHistorial();
    } catch (e) { console.error(e); }
  };

  const eliminarInteraccion = async (id) => {
    try {
      await axios.delete(`${API}/cats/translations/${id}`, { headers });
      setHistorial(prev => prev.filter(t => t.id !== id));
    } catch (e) { console.error(e); }
  };

  const onSonidoReproducido = (sonido) => {
    guardarInteraccion({
      tipo: 'sonido',
      frecuencia: 0,
      intensidad: 0,
      duracion: 0,
      interpretacion: sonido.label,
      interpretacion_id: sonido.id,
      emoji: sonido.emoji,
      confianza: 0,
    });
  };

  const analizarAudio = useCallback(() => {
    if (!analyserRef.current) return;
    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const freqArray = new Uint8Array(bufferLength);

    const update = () => {
      analyser.getByteTimeDomainData(dataArray);
      analyser.getByteFrequencyData(freqArray);
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) { const v = (dataArray[i] - 128) / 128; sum += v * v; }
      const rms = Math.sqrt(sum / bufferLength);
      setNivel(rms);

      let maxFreqIndex = 0, maxFreqValue = 0;
      for (let i = 0; i < bufferLength; i++) { if (freqArray[i] > maxFreqValue) { maxFreqValue = freqArray[i]; maxFreqIndex = i; } }
      const sampleRate = audioContextRef.current?.sampleRate || 44100;
      const dominantFreq = (maxFreqIndex * sampleRate) / (analyser.fftSize);

      audioDataRef.current.muestras.push({ rms, freq: dominantFreq });
      audioDataRef.current.intensidad = Math.max(audioDataRef.current.intensidad, rms);
      audioDataRef.current.frecuencia = dominantFreq;
      animFrameRef.current = requestAnimationFrame(update);
    };
    update();
  }, []);

  const iniciarGrabacion = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      audioDataRef.current = { frecuencia: 0, intensidad: 0, duracion: 0, muestras: [] };
      startTimeRef.current = Date.now();

      setGrabando(true);
      setResultado(null);
      setCountdown(5);
      analizarAudio();

      const countInterval = setInterval(() => {
        setCountdown(prev => { if (prev <= 1) { clearInterval(countInterval); return 0; } return prev - 1; });
      }, 1000);

      setTimeout(() => detenerGrabacion(), 5000);
    } catch (e) {
      console.error('Error accediendo al microfono:', e);
      alert('No se pudo acceder al microfono. Asegurate de dar permisos.');
    }
  };

  const detenerGrabacion = () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (audioContextRef.current) { audioContextRef.current.close(); audioContextRef.current = null; }

    const duracion = (Date.now() - startTimeRef.current) / 1000;
    audioDataRef.current.duracion = duracion;
    if (audioDataRef.current.muestras.length > 0) {
      audioDataRef.current.frecuencia = audioDataRef.current.muestras.reduce((s, m) => s + m.freq, 0) / audioDataRef.current.muestras.length;
    }

    const res = analizarMaullido(audioDataRef.current);
    setResultado(res);
    guardarInteraccion({
      tipo: 'traduccion',
      frecuencia: audioDataRef.current.frecuencia,
      intensidad: audioDataRef.current.intensidad,
      duracion: audioDataRef.current.duracion,
      interpretacion: res.interpretacion.texto,
      interpretacion_id: res.interpretacion.id,
      emoji: res.interpretacion.emoji,
      confianza: res.confianza,
    });
    setGrabando(false);
    setNivel(0);
    setCountdown(0);
  };

  const TABS = [
    { key: 'traducir', label: 'Traducir', icon: <Mic className="w-4 h-4" /> },
    { key: 'comunicar', label: 'Comunicar', icon: <Volume2 className="w-4 h-4" /> },
    { key: 'historial', label: 'Historial', icon: <History className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: 'var(--v-bg)' }}>
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="btn-volver">
          <ArrowLeft className="w-6 h-6" style={{ color: 'var(--v-text)' }} />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--v-gold)' }}>
            <Cat className="w-5 h-5" style={{ color: 'var(--v-bg)' }} />
          </div>
          <div>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Cinzel, serif', color: 'var(--v-gold)' }}>MK Pet Lab</h1>
            <p className="text-xs" style={{ color: 'var(--v-text-muted)' }}>Comunicate con tu mascota</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 mb-4">
        <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--v-border)' }}>
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className="flex-1 py-2.5 px-2 flex items-center justify-center gap-1.5 text-xs font-medium transition-colors"
              style={{
                backgroundColor: tab === t.key ? 'var(--v-gold)' : 'transparent',
                color: tab === t.key ? 'var(--v-bg)' : 'var(--v-text-muted)',
                fontFamily: 'Cinzel, serif',
              }}
            >
              {t.icon}{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 max-w-lg mx-auto">
        {tab === 'traducir' && (
          <div className="space-y-6">
            {!resultado ? (
              <div className="flex flex-col items-center pt-6 space-y-4">
                <BotonGrabar grabando={grabando} onClick={grabando ? detenerGrabacion : iniciarGrabacion} nivel={nivel} />
                {countdown > 0 && (
                  <p className="text-3xl font-bold" style={{ color: 'var(--v-gold)', fontFamily: 'Cinzel, serif' }}>{countdown}</p>
                )}
                <div className="text-center space-y-1 mt-2">
                  <p className="text-sm" style={{ color: 'var(--v-text-muted)' }}>Pulsa y acerca el telefono a tu gato</p>
                  <p className="text-xs" style={{ color: 'var(--v-text-muted)' }}>Grabacion de 5 segundos</p>
                </div>
              </div>
            ) : (
              <ResultadoTraduccion resultado={resultado} onClose={() => setResultado(null)} />
            )}
          </div>
        )}

        {tab === 'comunicar' && (
          <PanelComunicar onPlay={onSonidoReproducido} />
        )}

        {tab === 'historial' && (
          <HistorialPanel historial={historial} onDelete={eliminarInteraccion} />
        )}
      </div>
    </div>
  );
}
